import { useState, useEffect, useCallback } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Check, X } from "lucide-react"
import axios from "axios"
import { Organization } from "@/modules/organizations/entities/Organization"
import { redirectToPayFast } from "@/modules/subscriptions/infrastructure/payfast-redirect"
import BillingPlans from "@/modules/subscriptions/ui/components/BillingPlans"
import Checkout from "@/modules/subscriptions/ui/components/Checkout"
import type { BillingPlanDto } from "@/modules/subscriptions/dtos/billing-plan.dto"
import { useSubscription } from "@/modules/subscriptions/hooks/use-subscription"
import { useSubscriptionStatus } from "@/modules/subscriptions/hooks/use-subscription-status"
import { useOrganizationsService } from "@/modules/organizations/hooks/use-organizations"
import { useValidateCoupon } from "@/modules/coupon/hooks/use-coupon"
import type { CouponValidationDto } from "@/modules/coupon/dtos/coupon.dto"
import { toast } from "sonner"
import { Button } from "@/modules/shared/ui/components/button"
import { SwitchPlanConfirmationModal } from "@/modules/subscriptions/ui/components/SwitchPlanConfirmationModal"
import { setPendingPaymentReturn } from "@/lib/payment-return"

enum SubscriptionStep {
  PlanSelection = 1,
  Checkout,
}

interface ActivateSubscriptionModalProps {
  isOpen: boolean
  onClose: () => void
  organization: Organization | null
  onSubscriptionActivated?: () => void
}

export default function ActivateSubscriptionModal({
  isOpen,
  onClose,
  organization,
}: ActivateSubscriptionModalProps) {
  const navigate = useNavigate()
  const { loadOrganizations, setOrganizationById } = useOrganizationsService()
  const { activate, isLoading } = useSubscription()
  const { data: currentSubscription } = useSubscriptionStatus(organization?.id ?? undefined)

  const [currentStep, setCurrentStep] = useState<SubscriptionStep>(
    SubscriptionStep.PlanSelection,
  )
  const [selectedPlan, setSelectedPlan] = useState<BillingPlanDto | null>(null)
  const [isYearly, setIsYearly] = useState(true)
  const [appliedCoupon, setAppliedCoupon] = useState<CouponValidationDto | null>(null)
  const [inlineError, setInlineError] = useState<string | null>(null)
  const [showSwitchConfirm, setShowSwitchConfirm] = useState(false)
  const [processingMessage, setProcessingMessage] = useState("Processing your request")

  const { mutateAsync: validateCoupon } = useValidateCoupon()

  const activeSubscription = currentSubscription?.hasSubscription ? currentSubscription : null
  const isDifferentPlan =
    activeSubscription != null &&
    selectedPlan != null &&
    activeSubscription.planName !== selectedPlan.name

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(SubscriptionStep.PlanSelection)
      setSelectedPlan(null)
      setAppliedCoupon(null)
      setInlineError(null)
    }
  }, [isOpen])

  useEffect(() => {
    setInlineError(null)
  }, [currentStep])

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget && !isLoading) onClose()
    },
    [isLoading, onClose],
  )

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isLoading) onClose()
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [isOpen, isLoading, onClose])

  if (!isOpen || !organization) return null

  const handlePlanSelect = (plan: BillingPlanDto, yearly: boolean) => {
    setSelectedPlan(plan)
    setIsYearly(yearly)
    setCurrentStep(SubscriptionStep.Checkout)
  }

  const executeActivation = async () => {
    if (!selectedPlan || !organization) return

    setInlineError(null)

    try {
      const billingInterval: 1 | 12 = isYearly ? 12 : 1

      const response = await activate({
        planId: String(selectedPlan.id),
        billingInterval,
        organizationId: organization.id,
        couponCode: appliedCoupon?.couponCode ?? null,
      })

      if (response.trialActivated) {
        toast.success("Your trial has been activated")
        try {
          await loadOrganizations()
        } catch {
          // Non-fatal
        }
        setOrganizationById(organization.id)
        navigate("/dashboard", { replace: true })
        return
      }

      setPendingPaymentReturn({ organizationId: organization.id, returnPath: "/dashboard" })
      if (response.paymentRedirect) {
        setProcessingMessage("Redirecting to payment gateway")
        redirectToPayFast(
          response.paymentRedirect.redirectUrl,
          response.paymentRedirect.formData,
        )
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        const msg = (error.response.data as { message?: string })?.message
        setInlineError(msg || "Invalid subscription details. Please try again.")
      } else {
        toast.error("Failed to start payment process. Please try again.")
      }
    }
  }

  const handlePayment = () => {
    if (!selectedPlan || !organization) return

    if (isDifferentPlan) {
      setShowSwitchConfirm(true)
      return
    }

    void executeActivation()
  }

  const handleConfirmSwitch = () => {
    setShowSwitchConfirm(false)
    void executeActivation()
  }

  const submitLabel = isDifferentPlan ? "Switch Plan" : "Activate Plan"

  const companyForm = {
    companyName: organization.name,
    streetAddress: organization.physicalAddress?.line1 || "",
    city: organization.physicalAddress?.city || "",
    state: organization.physicalAddress?.state || "",
    zipCode: organization.physicalAddress?.postalCode || "",
    country: organization.physicalAddress?.country || "",
    line2: organization.physicalAddress?.line2,
  }

  return (
    <>
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-md flex items-center justify-center z-50"
        onClick={handleBackdropClick}
      >
        <div className="bg-card rounded-lg shadow-xl w-full max-w-6xl mx-4 max-h-[90vh] overflow-y-auto animate-modal-pop">
          <div className="flex justify-between items-center p-6 border-b">
            <h2 className="text-2xl font-bold">
              {currentStep === SubscriptionStep.PlanSelection &&
                "Choose A Pricing Plan"}
              {currentStep === SubscriptionStep.Checkout && "Review & Complete"}
            </h2>
            <Link
              onClick={isLoading ? undefined : onClose}
              className={`p-1 rounded-md transition-colors ${isLoading ? "pointer-events-none text-muted-foreground/40" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}
              aria-label="Close"
              to={""}
            >
              <X size={20} />
            </Link>
          </div>

          {/* Progress Indicator */}
          <div className="flex items-center justify-center border-b px-6 py-3">
            <div className="flex items-center gap-3">
              {/* Step 1 */}
              <div className="flex items-center gap-2">
                <div
                  className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition-colors ${currentStep >= SubscriptionStep.PlanSelection
                      ? "bg-primary text-primary-foreground"
                      : "border-2 border-border text-muted-foreground"
                    }`}
                >
                  {currentStep > SubscriptionStep.PlanSelection ? (
                    <Check size={13} />
                  ) : (
                    "1"
                  )}
                </div>
                <span
                  className={`text-sm font-medium transition-colors ${currentStep >= SubscriptionStep.PlanSelection
                      ? "text-foreground"
                      : "text-muted-foreground"
                    }`}
                >
                  Plan Selection
                </span>
              </div>

              {/* Connector */}
              <div
                className={`h-0.5 w-8 transition-colors ${currentStep > SubscriptionStep.PlanSelection
                    ? "bg-primary"
                    : "bg-border"
                  }`}
              />

              {/* Step 2 */}
              <div className="flex items-center gap-2">
                <div
                  className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition-colors ${currentStep >= SubscriptionStep.Checkout
                      ? "bg-primary text-primary-foreground"
                      : "border-2 border-border text-muted-foreground"
                    }`}
                >
                  2
                </div>
                <span
                  className={`text-sm font-medium transition-colors ${currentStep >= SubscriptionStep.Checkout
                      ? "text-foreground"
                      : "text-muted-foreground"
                    }`}
                >
                  Checkout
                </span>
              </div>
            </div>
          </div>

          <div className="p-6">
            {currentStep === SubscriptionStep.PlanSelection && (
              <div className="max-w-6xl mx-auto">
                <p className="text-center mb-8 text-muted-foreground">
                  Select a plan to activate subscription for {organization.name}
                </p>
                <BillingPlans onPlanSelect={handlePlanSelect} />
                <div className="flex justify-start mt-6">
                  <Button
                    onClick={onClose}
                    variant="ghost"
                    className="px-6 py-2 text-primary hover:text-foreground hover:bg-transparent"
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {currentStep === SubscriptionStep.Checkout && selectedPlan && (
              <Checkout
                companyForm={companyForm}
                selectedPlan={selectedPlan}
                isYearly={isYearly}
                onBack={() => setCurrentStep(SubscriptionStep.PlanSelection)}
                onComplete={handlePayment}
                isProcessing={isLoading}
                appliedCoupon={appliedCoupon}
                onCouponChange={setAppliedCoupon}
                onCouponValidate={(code) =>
                  validateCoupon({ code, organizationId: organization.id })
                }
                submitLabel={submitLabel}
                processingMessage={processingMessage}
                errorMessage={inlineError ?? undefined}
              />
            )}
          </div>
        </div>
      </div>

      <SwitchPlanConfirmationModal
        open={showSwitchConfirm}
        onOpenChange={setShowSwitchConfirm}
        activeSubscription={activeSubscription}
        selectedPlan={selectedPlan}
        isLoading={isLoading}
        onConfirm={handleConfirmSwitch}
      />
    </>
  )
}
